import { MESSAGE_STATUS_LABELS } from '@crm/shared';
import { Badge, Button, cn, Input, Spinner, Textarea } from '@crm/ui';
import {
  ArrowLeft,
  Bot,
  CalendarClock,
  CalendarX,
  Check,
  CheckCheck,
  Clock,
  MessageSquare,
  RefreshCw,
  Search,
  Send,
  TriangleAlert,
  UserRound,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar, EmptyState, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useSetHandling } from '@/modules/bot/api';
import { CUSTOMERS_ROUTES } from '@/modules/customers/api';
import {
  type ChatMessage,
  type Conversation,
  type ConversationStatus,
  useConversation,
  useConversationMessages,
  useConversations,
  useMarkRead,
  useSendMessage,
} from '../api';
import { AppointmentsPanel, type PanelMode } from '../components/appointments-panel';

const ABAS: { key: ConversationStatus | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'Todas' },
  { key: 'PENDING', label: 'Pendentes' },
  { key: 'OPEN', label: 'Abertas' },
];

const hora = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const diaCompleto = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function statusIcon(message: ChatMessage) {
  if (message.status === 'FAILED')
    return <TriangleAlert className="h-3.5 w-3.5 text-destructive" />;
  if (message.status === 'QUEUED') return <Clock className="h-3.5 w-3.5 opacity-70" />;
  if (message.status === 'SENT') return <Check className="h-3.5 w-3.5 opacity-70" />;
  if (message.status === 'DELIVERED') return <CheckCheck className="h-3.5 w-3.5 opacity-70" />;

  return <CheckCheck className="h-3.5 w-3.5 text-sky-400" />;
}

function ConversationItem({
  conversation,
  active,
  onSelect,
}: {
  conversation: Conversation;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 border-b border-border px-3 py-3 text-left transition-colors hover:bg-muted/60',
        active && 'bg-muted',
      )}
    >
      <Avatar name={conversation.displayName} image={conversation.customerImage} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate font-medium">{conversation.displayName}</span>
          {conversation.lastMessageAt ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              {hora.format(new Date(conversation.lastMessageAt))}
            </span>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm text-muted-foreground">
            {conversation.lastMessagePreview ?? conversation.contactPhone}
          </span>
          {conversation.unreadCount > 0 ? (
            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
              {conversation.unreadCount}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  const saiu = message.direction === 'OUTBOUND';

  return (
    <div className={cn('flex', saiu ? 'justify-end' : 'justify-start')}>
      {/* A perninha do balao e um quadrado girado, encaixado no canto de baixo
          e escondido pela metade - e o desenho que todo mundo reconhece do
          proprio WhatsApp, sem imagem nem SVG. */}
      <div
        className={cn(
          'relative max-w-[75%] px-3 py-2 shadow-sm',
          saiu
            ? 'rounded-2xl rounded-br-sm bg-primary text-primary-foreground after:right-[-5px] after:bg-primary'
            : 'rounded-2xl rounded-bl-sm border border-border bg-card after:left-[-5px] after:border-b after:border-l after:border-border after:bg-card',
          'after:absolute after:bottom-0 after:h-3 after:w-3 after:rotate-45 after:content-[""]',
        )}
      >
        <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
        <div
          className={cn(
            'mt-1 flex items-center justify-end gap-1 text-[11px]',
            saiu ? 'text-primary-foreground/80' : 'text-muted-foreground',
          )}
        >
          {message.origin === 'AUTOMATIC' && saiu ? <span>automatica</span> : null}
          {message.senderName ? <span>{message.senderName}</span> : null}
          <span>{hora.format(new Date(message.createdAt))}</span>
          {saiu ? (
            <span title={message.errorMessage ?? MESSAGE_STATUS_LABELS[message.status].label}>
              {statusIcon(message)}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Thread({ conversationId }: { conversationId: string }) {
  const { data, isLoading } = useConversationMessages(conversationId);
  const fim = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'end' });
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const mensagens = data?.data ?? [];
  let ultimoDia = '';

  return (
    <div className="flex-1 space-y-2 overflow-y-auto bg-muted/40 p-4">
      {mensagens.map((message) => {
        const dia = diaCompleto.format(new Date(message.createdAt));
        const separador = dia !== ultimoDia;
        ultimoDia = dia;

        return (
          <div key={message.id} className="space-y-2">
            {separador ? (
              <div className="flex justify-center">
                <span className="rounded-full bg-background px-3 py-1 text-xs text-muted-foreground">
                  {dia}
                </span>
              </div>
            ) : null}
            <Bubble message={message} />
          </div>
        );
      })}
      <div ref={fim} />
    </div>
  );
}

function Chat({ conversationId, onBack }: { conversationId: string; onBack: () => void }) {
  const navigate = useNavigate();
  const { data: conversation } = useConversation(conversationId);
  const send = useSendMessage(conversationId);
  const handling = useSetHandling(conversationId);
  const { mutate: marcarLida } = useMarkRead();
  const [texto, setTexto] = useState('');
  const [painel, setPainel] = useState<PanelMode | null>(null);

  useEffect(() => {
    marcarLida(conversationId);
  }, [conversationId, marcarLida]);

  if (!conversation) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const semCadastro = !conversation.customerId;

  async function enviar() {
    const corpo = texto.trim();
    if (!corpo) return;

    await send.mutateAsync(corpo);
    setTexto('');
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-2 border-b border-border px-3 py-3 sm:gap-3 sm:px-4">
        <Button
          size="icon"
          variant="ghost"
          className="shrink-0 md:hidden"
          aria-label="Voltar para as conversas"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Avatar name={conversation.displayName} image={conversation.customerImage} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{conversation.displayName}</p>
          <p className="truncate text-xs text-muted-foreground">
            {conversation.contactPhone} · {conversation.unitName}
          </p>
        </div>
        {conversation.withBot ? (
          <Badge className="hidden bg-primary/10 text-primary sm:inline-flex">Assistente</Badge>
        ) : null}
        {semCadastro ? (
          <Badge variant="warning" className="hidden sm:inline-flex">
            Sem cadastro
          </Badge>
        ) : null}

        {/* Assumir cala o assistente; devolver o traz de volta. E o botao que
            o balcao procura quando quer entrar na conversa. */}
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0"
          disabled={handling.isPending}
          onClick={() => handling.mutate(!conversation.handledByHuman)}
        >
          {conversation.handledByHuman ? (
            <>
              <Bot className="h-4 w-4" />
              <span className="hidden sm:inline">Devolver ao assistente</span>
            </>
          ) : (
            <>
              <UserRound className="h-4 w-4" />
              <span className="hidden sm:inline">Assumir</span>
            </>
          )}
        </Button>
      </header>

      <Thread conversationId={conversationId} />

      <div className="border-t border-border p-3">
        {conversation.canSendFreeform ? null : (
          <p className="mb-2 rounded-card bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            Fora da janela de 24h. So e possivel responder depois que o cliente escrever de novo.
          </p>
        )}

        <div className="flex items-end gap-2">
          <Textarea
            rows={2}
            value={texto}
            placeholder="Escreva uma mensagem"
            disabled={!conversation.canSendFreeform || send.isPending}
            onChange={(event) => setTexto(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void enviar();
              }
            }}
          />
          <Button
            aria-label="Enviar"
            className="shrink-0 px-3 sm:px-4"
            onClick={() => void enviar()}
            disabled={!conversation.canSendFreeform || send.isPending || !texto.trim()}
          >
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Enviar</span>
          </Button>
        </div>

        {send.error ? (
          <p className="mt-2 text-xs text-destructive">{toErrorMessage(send.error)}</p>
        ) : null}

        {/* No celular as acoes rolam na horizontal: quebradas em duas linhas
            elas comiam a altura do historico, que e o que importa na tela. */}
        <div className="no-scrollbar -mx-3 mt-3 flex gap-2 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:px-0">
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            disabled={semCadastro}
            onClick={() => setPainel('list')}
          >
            <CalendarClock className="h-4 w-4" />
            Agendamentos
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            disabled={semCadastro}
            onClick={() => setPainel('cancel')}
          >
            <CalendarX className="h-4 w-4" />
            Cancelar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            disabled={semCadastro}
            onClick={() => setPainel('reschedule')}
          >
            <RefreshCw className="h-4 w-4" />
            Reagendar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            onClick={() =>
              navigate(
                conversation.customerId
                  ? CUSTOMERS_ROUTES.detail(conversation.customerId)
                  : CUSTOMERS_ROUTES.NEW,
              )
            }
          >
            <UserRound className="h-4 w-4" />
            {semCadastro ? 'Cadastrar cliente' : 'Cadastro'}
          </Button>
        </div>
      </div>

      {painel && conversation.customerId ? (
        <AppointmentsPanel
          customerId={conversation.customerId}
          mode={painel}
          onClose={() => setPainel(null)}
        />
      ) : null}
    </div>
  );
}

/** Atendimento por WhatsApp: a lista de conversas e o chat com o cliente. */
export function ConversationsPage() {
  useDocumentTitle('Atendimento por WhatsApp');

  // O funil manda o id na URL: clicar no card do lead abre a conversa dele.
  const [searchParams] = useSearchParams();
  const [aba, setAba] = useState<ConversationStatus | 'ALL'>('ALL');
  const [busca, setBusca] = useState('');
  const [selecionada, setSelecionada] = useState<string | null>(searchParams.get('c'));

  const { data, isLoading } = useConversations({
    status: aba === 'ALL' ? undefined : aba,
    q: busca.trim().length >= 2 ? busca.trim() : undefined,
  });

  const conversas = useMemo(() => data?.data ?? [], [data]);

  // No celular a tela e uma coisa de cada vez: a lista, ou a conversa. Abrir
  // uma sozinha no telefone jogaria o atendente dentro de um chat que ele nao
  // escolheu.
  useEffect(() => {
    const primeira = conversas[0];
    const cabemOsDois = window.matchMedia('(min-width: 768px)').matches;
    if (cabemOsDois && !selecionada && primeira) setSelecionada(primeira.id);
  }, [conversas, selecionada]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Atendimento por WhatsApp"
        description="As conversas com os clientes, com o historico do que foi enviado e recebido."
        icon={<MessageSquare />}
      />

      <div className="grid h-[calc(100dvh-13rem)] min-h-[26rem] grid-cols-1 overflow-hidden rounded-card border border-border bg-card md:grid-cols-[20rem_1fr]">
        <aside
          className={cn(
            'min-h-0 flex-col border-border md:flex md:border-r',
            selecionada ? 'hidden' : 'flex',
          )}
        >
          <div className="space-y-2 border-b border-border p-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Procurar por nome, telefone ou mensagem"
                className="pl-8"
              />
            </div>
            <div className="flex gap-1">
              {ABAS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setAba(item.key)}
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                    aba === item.key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground',
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : conversas.length === 0 ? (
              <EmptyState icon={<MessageSquare />} title="Nenhuma conversa por aqui" />
            ) : (
              conversas.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  active={conversation.id === selecionada}
                  onSelect={() => setSelecionada(conversation.id)}
                />
              ))
            )}
          </div>
        </aside>

        {selecionada ? (
          <Chat
            key={selecionada}
            conversationId={selecionada}
            onBack={() => setSelecionada(null)}
          />
        ) : (
          <div className="hidden items-center justify-center md:flex">
            <EmptyState icon={<MessageSquare />} title="Escolha uma conversa" />
          </div>
        )}
      </div>
    </div>
  );
}
